import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { AlertCircle, Card, EmptyState, Pagination, SearchLg, Table, Users01 } from "@opendate-app/setlist";
import { customSegmentColumns } from "./CustomSegmentRow";
import DeleteSegmentModal from "./DeleteSegmentModal";
import { toastError, toastSuccess } from "../../../shared/toastHelper";

const CustomSegmentsList = ({ dataSource, teamId, csrfToken, searchTerm, onResultCount }) => {
  const searching = Boolean(searchTerm);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total_entries: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);

  // Reset page to 1 whenever searchTerm changes, adjusted during render (not
  // in a separate effect) so the fetch effect below only ever runs once per
  // filter change, with the corrected page — never a stale-page request.
  const [prevSearchTerm, setPrevSearchTerm] = useState(searchTerm);
  if (prevSearchTerm !== searchTerm) {
    setPrevSearchTerm(searchTerm);
    if (page !== 1) setPage(1);
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    axios
      .get(dataSource, { params: { page, per_page: 10, q: searchTerm || undefined } })
      .then(({ data }) => {
        if (!active) return;
        setRows(data.data || []);
        setPagination(data.pagination || {});
        if (onResultCount) onResultCount(data.pagination ? data.pagination.total_entries : 0);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setRows([]);
        setPagination({ current_page: 1, per_page: 10, total_entries: 0, total_pages: 1 });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [dataSource, page, searchTerm, reloadToken]);

  const handleEdit = (segment) => {
    const path = segment.type === "Segments::Manual"
      ? `/teams/${teamId}/segments/${segment.id}`
      : `/teams/${teamId}/segments/${segment.id}/edit`;
    window.location.href = path;
  };

  const handleConfirmDelete = (segment) => {
    setPendingDelete(null);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.delete(`/teams/${teamId}/segments/${segment.id}`)
      .then(() => {
        toastSuccess("Segment deleted successfully");
        if (rows.length === 1 && page > 1) {
          setPage(page - 1);
        } else {
          setReloadToken((t) => t + 1);
        }
      })
      .catch(() => toastError("Could not delete segment, please try again"));
  };

  const renderBody = () => {
    if (error) {
      return <EmptyState size="sm" icon={AlertCircle} title="Couldn’t load segments" description="Please try again." />;
    }
    if (rows.length === 0 && !loading) {
      return (
        <EmptyState
          size="sm"
          icon={searching ? SearchLg : Users01}
          title={searching ? `No segments match “${searchTerm}”.` : "No custom segments yet."}
        />
      );
    }
    return (
      <div style={{ padding: "4px 8px" }}>
        <Table
          columns={customSegmentColumns(teamId, handleEdit, setPendingDelete)}
          data={rows}
          getRowKey={(segment) => segment.id}
          loading={loading && rows.length === 0}
          loadingLabel="Loading segments"
          stackBelow={640}
          stickyLastColumn
        />
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Card padding="none">{renderBody()}</Card>

      <Pagination
        currentPage={pagination.current_page}
        totalPages={pagination.total_pages}
        onPageChange={(p) => { if (!loading) setPage(p); }}
      />

      <DeleteSegmentModal
        segment={pendingDelete}
        team={{ id: teamId }}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

CustomSegmentsList.propTypes = {
  dataSource: PropTypes.string.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  csrfToken: PropTypes.string.isRequired,
  searchTerm: PropTypes.string,
  onResultCount: PropTypes.func,
};

export default CustomSegmentsList;
